import { Box, CircularProgress, CircularProgressProps, Typography } from "@mui/material";
import { Signal, useSignal } from "@preact/signals-react";
import { Fragment, PropsWithChildren } from "react";
import { CircularProgressVariant, FlexProperties, Maybe, StyleProperties } from "../../../resolvers-types";
import { INGCircularProgressProps, RuntimeContext } from "../../library/NGFieldExtensions";
import { setupLocalState } from "../../library/dataService";
import { getTypename } from "../../library/metadataUtils";
import { getClassName, getsxObject, getTestId } from "../../library/utils";

interface LocalState {
  config: CircularProgressProps;

  Classes: Signal<string[]>;
  DisableShrink: Signal<boolean>;
  ShowFullRing: Signal<boolean>;
  ShowValue: Signal<boolean>;
  Size: Signal<CircularProgressProps["size"]>;
  Style: Signal<Maybe<StyleProperties> | Maybe<FlexProperties> | undefined>;
  Thickness: Signal<CircularProgressProps["thickness"]>;
  Value: Signal<number>;
  Variant: Signal<CircularProgressVariant>;
  Visible: Signal<boolean>;

  context: RuntimeContext;
}

export function NGCircularProgress({ config, context }: INGCircularProgressProps) {
  // local state
  const local: LocalState = setupLocalState(
    config,
    {
      Classes: useSignal(config.Classes),
      DisableShrink: useSignal(config.DisableShrink),
      ShowFullRing: useSignal(config.ShowFullRing ?? true),
      Size: useSignal(config.Size),
      ShowValue: useSignal(config.ShowValue),
      Style: useSignal(config.Style ?? {}),
      Thickness: useSignal(config.Thickness),
      Value: useSignal(config.Value ?? 0),
      Variant: useSignal<CircularProgressVariant>(config.Variant ?? CircularProgressVariant.Indeterminate),
      Visible: useSignal(config.Visible ?? true),
    },
    context
  );

  // variables
  const rawSize = local.Size.value;
  const sizeCss = typeof rawSize === "number" ? `${rawSize}px` : rawSize ?? 40;
  const labelFontSize = `calc(${sizeCss} * 0.2)`;
  const labelLineHeight = `calc(${sizeCss} * 0.5)`;
  const className = getClassName(local.Classes) || "color-primary";

  // wrapper for the value
  const WrapperWithValue = ({ children }: PropsWithChildren) => (
    <Box
      className={className}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
      }}
    >
      {children}
      <Box component="span" sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}>
        <Typography
          color="inherit"
          fontSize={labelFontSize}
          fontWeight="bold"
          lineHeight={labelLineHeight}
          sx={{ userSelect: "none" }}
          textAlign="center"
          variant="caption"
        >
          {Math.round(local.Value.value)}%
        </Typography>
      </Box>
    </Box>
  );

  const shouldShowValue = local.ShowValue.value && local.Variant.value === CircularProgressVariant.Determinate;
  const WrapperToUse = shouldShowValue ? WrapperWithValue : Fragment;

  // render
  return local.Visible.value ? (
    <WrapperToUse>
      <div style={{ position: "relative", display: "inline-flex" }}>
        {local.ShowFullRing.value && (<CircularProgress
            variant={local.Variant.value}
            size={local.Size.value}
            thickness={local.Thickness.value}
            value={100}
            sx={{
              ...getsxObject(local.Style.value),
              color: (theme) => theme.palette.grey[300],
              position: "absolute"
            }}
            />)}
        <CircularProgress
          data-testid={getTestId(config)}
          data-type={getTypename(config)}
          className={className}
          color="inherit"
          disableShrink={local.DisableShrink.value}
          size={local.Size.value}
          sx={getsxObject(local.Style.value)}
          thickness={local.Thickness.value}
          value={local.Value.value}
          variant={local.Variant.value}
          />
        </div>
    </WrapperToUse>
  ) : null;
}
